import { supabase } from '../lib/api/supabase';

/**
 * Settings Service - Handles app settings and configuration
 *
 * Schema: app_settings table
 * Fields: id (UUID, PK), setting_key (string, unique), setting_value (JSONB),
 *         created_at, updated_at
 *
 * Keys: general_settings, ui_settings, status_config, stock_settings
 */

export const settingsService = {
  /**
   * Fetch all settings
   */
  async fetchSettings() {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },

  /**
   * Fetch setting by key
   */
  async fetchSetting(key) {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .eq('setting_key', key)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Create or update setting
   */
  async upsertSetting(key, value) {
    const { data, error } = await supabase
      .from('app_settings')
      .upsert([{ setting_key: key, setting_value: value }], { onConflict: 'setting_key' })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update setting
   */
  async updateSetting(key, updates) {
    const { data, error } = await supabase
      .from('app_settings')
      .update(updates)
      .eq('setting_key', key)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete setting
   */
  async deleteSetting(key) {
    const { error } = await supabase
      .from('app_settings')
      .delete()
      .eq('setting_key', key);

    if (error) throw error;
  },

  /**
   * Get general settings
   */
  async getGeneralSettings() {
    const setting = await this.fetchSetting('general_settings');
    return setting?.setting_value || {};
  },

  /**
   * Update general settings
   */
  async updateGeneralSettings(settings) {
    return this.upsertSetting('general_settings', settings);
  },

  /**
   * Get status configuration
   */
  async getStatusConfig() {
    const setting = await this.fetchSetting('status_config');
    return setting?.setting_value || {
      statusIcons: {},
      statusColors: {}
    };
  },

  /**
   * Update status configuration
   */
  async updateStatusConfig(config) {
    return this.upsertSetting('status_config', config);
  },

  /**
   * Get stock settings
   */
  async getStockSettings() {
    const setting = await this.fetchSetting('stock_settings');
    return setting?.setting_value || {
      oilCategories: ['Men', 'Women', 'Unisex'],
      bottleVariants: {},
      lowStockThresholds: { oils: 50, bottles: 10 }
    };
  },

  /**
   * Update stock settings
   */
  async updateStockSettings(settings) {
    return this.upsertSetting('stock_settings', settings);
  },

  /**
   * Get UI settings (logo, primary color, etc.)
   */
  async getUISettings() {
    const setting = await this.fetchSetting('ui_settings');
    return setting?.setting_value || {};
  },

  /**
   * Update UI settings
   */
  async updateUISettings(settings) {
    return this.upsertSetting('ui_settings', settings);
  },

  /**
   * Get logo URL from ui_settings
   */
  async getLogoUrl() {
    const setting = await this.fetchSetting('ui_settings');
    return setting?.setting_value?.logoUrl || '';
  },

  /**
   * Update logo URL in ui_settings
   */
  async updateLogoUrl(url) {
    const uiSettings = await this.fetchSetting('ui_settings');
    const currentSettings = uiSettings?.setting_value || {};
    return this.upsertSetting('ui_settings', { ...currentSettings, logoUrl: url });
  },

  /**
   * Subscribe to settings changes (real-time)
   */
  subscribeToSettings(callback) {
    const subscription = supabase
      .from('app_settings')
      .on('*', payload => {
        callback(payload);
      })
      .subscribe();
    
    return subscription;
  }
};

